/**
 * Local RxDB (Dexie) collection: `site_visit_recording_sessions` on database `mpowerwwa`.
 * Stores session metadata (timestamps, duration, status, chunkCount, totalBytes).
 * Audio bytes live in IndexedDB `site_visit_recording_audio` (see siteVisitRecordingChunkStore.ts).
 * Inspect in DevTools → Application → IndexedDB, or `window.db` after DB init.
 */
import { getDatabase } from '@rxdb/config/databaseConfig';
import type { RxCollection } from 'rxdb';

export type SiteVisitRecordingSessionStatus = 'idle' | 'recording' | 'paused' | 'completed';

export interface SiteVisitRecordingSessionRow {
	id: string;
	/** Existing site lead id from Site Summary / Add New Visit flow (not sitenumber_c). */
	siteId: number | null;
	status: SiteVisitRecordingSessionStatus;
	accumulatedMs: number;
	runningSince: number | null;
	recordingStartedAt: number | null;
	completedAt: number | null;
	finalDurationMs: number | null;
	/** Number of blobs stored in IndexedDB `site_visit_recording_audio.chunks`. */
	chunkCount: number;
	/** Sum of chunk byte sizes in IndexedDB. */
	totalBytes: number;
	mimeType: string | null;
	updatedAt: number;
	uploadOnStop: boolean;
}

type DbWithRecordingSessions = {
	site_visit_recording_sessions: RxCollection<SiteVisitRecordingSessionRow>;
};

export async function persistSiteVisitRecordingSession(row: SiteVisitRecordingSessionRow): Promise<void> {
	const db = (await getDatabase()) as unknown as DbWithRecordingSessions;
	const col = db.site_visit_recording_sessions;
	const existing = await col.findOne(row.id).exec();
	if (existing) {
		await existing.patch(row);
	} else {
		await col.insert(row);
	}
}

const INTERRUPTED_STATUSES: SiteVisitRecordingSessionStatus[] = ['recording', 'paused'];

/** Latest in-progress form session (reload mid-recording) that still has IndexedDB chunks. */
export async function findInterruptedSiteVisitRecordingSession(
	siteId: number
): Promise<SiteVisitRecordingSessionRow | null> {
	const { getRecordingChunksStats } = await import('@services/siteVisitRecording/siteVisitRecordingChunkStore');
	const db = (await getDatabase()) as unknown as DbWithRecordingSessions;
	const col = db.site_visit_recording_sessions;
	const docs = await col
		.find({
			selector: {
				siteId,
				status: { $in: INTERRUPTED_STATUSES },
			},
		})
		.sort({ updatedAt: 'desc' })
		.exec();

	for (const doc of docs) {
		const row = doc.toJSON() as SiteVisitRecordingSessionRow;
		try {
			const stats = await getRecordingChunksStats(row.id);
			if (stats.chunkCount > 0 && stats.totalBytes > 0) {
				return row;
			}
		} catch {
			// try next session
		}
	}
	return null;
}

const PENDING_UPLOAD_STATUSES: SiteVisitRecordingSessionStatus[] = ['completed'];

/**
 * Latest finished-but-not-yet-uploaded form/draft session (e.g. Submit failed or was interrupted
 * before upload) that still has its audio chunks in IndexedDB. Used to avoid discarding a
 * recording on reload when it has not been persisted to the backend yet.
 */
export async function findPendingUploadSiteVisitRecordingSession(
	siteId: number
): Promise<SiteVisitRecordingSessionRow | null> {
	const { getRecordingChunksStats } = await import('@services/siteVisitRecording/siteVisitRecordingChunkStore');
	const db = (await getDatabase()) as unknown as DbWithRecordingSessions;
	const col = db.site_visit_recording_sessions;
	const docs = await col
		.find({
			selector: {
				siteId,
				status: { $in: PENDING_UPLOAD_STATUSES },
			},
		})
		.sort({ updatedAt: 'desc' })
		.exec();

	for (const doc of docs) {
		const row = doc.toJSON() as SiteVisitRecordingSessionRow;
		try {
			const stats = await getRecordingChunksStats(row.id);
			if (stats.chunkCount > 0 && stats.totalBytes > 0) {
				return row;
			}
		} catch {
			// try next session
		}
	}
	return null;
}

export async function listSiteVisitRecordingSessionsForSite(siteId: number): Promise<SiteVisitRecordingSessionRow[]> {
	const db = (await getDatabase()) as unknown as DbWithRecordingSessions;
	const col = db.site_visit_recording_sessions;
	const docs = await col.find({ selector: { siteId } }).exec();
	return docs.map((doc) => doc.toJSON() as SiteVisitRecordingSessionRow);
}

export async function removeSiteVisitRecordingSession(sessionId: string): Promise<void> {
	const db = (await getDatabase()) as unknown as DbWithRecordingSessions;
	const col = db.site_visit_recording_sessions;
	const doc = await col.findOne(sessionId).exec();
	if (doc) {
		await doc.remove();
	}
}

/**
 * Removes prior visit recordings for this site (chunks + RxDB rows).
 * Call before a new recording; skip `exceptSessionId` when resuming after reload.
 */
export async function cleanupSiteVisitRecordingDataForSite(siteId: number, exceptSessionId?: string): Promise<void> {
	const { deleteRecordingChunksForSession } = await import('@services/siteVisitRecording/siteVisitRecordingChunkStore');
	const rows = await listSiteVisitRecordingSessionsForSite(siteId);
	await Promise.all(
		rows.map(async (row) => {
			if (exceptSessionId && row.id === exceptSessionId) {
				return;
			}
			await deleteRecordingChunksForSession(row.id);
			await removeSiteVisitRecordingSession(row.id);
		})
	);
}

/** Moves local session metadata from a provisional site id to the real site lead id. */
export async function reassignSiteVisitRecordingSessionsSiteId(fromSiteId: number, toSiteId: number): Promise<void> {
	if (fromSiteId === toSiteId) {
		return;
	}
	const rows = await listSiteVisitRecordingSessionsForSite(fromSiteId);
	const now = Date.now();
	await Promise.all(
		rows.map(async (row) => {
			await persistSiteVisitRecordingSession({ ...row, siteId: toSiteId, updatedAt: now });
		})
	);
}
